import React, { useEffect, useState, useCallback } from "react";
import AdminSidebar from "../../components/AdminSidebar";
import { useToast } from "../../components/Toast.jsx";
import "./admin.css";

export default function AdminTowerRequests() {
  const toast = useToast();
  const [activeTab, setActiveTab] = useState("pending"); // "pending", "approved"
  const [towerAdmins, setTowerAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const API = process.env.REACT_APP_API_URL;
  const token = localStorage.getItem("token");

  const loadTowerAdmins = useCallback(async () => {
    try {
      setLoading(true);
      setError("");

      const endpoint =
        activeTab === "pending"
          ? "/api/admin/tower-admin/pending"
          : "/api/admin/tower-admin/all";

      const res = await fetch(`${API}${endpoint}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) {
        setError("Failed to load tower admins");
        return;
      }

      const data = await res.json();
      if (activeTab === "approved") {
        // Filter to show only approved admins
        setTowerAdmins(data.filter((a) => a.status === "approved"));
      } else {
        setTowerAdmins(data);
      }
    } catch (err) {
      setError("Server error while loading requests");
    } finally {
      setLoading(false);
    }
  }, [activeTab, API, token]);

  useEffect(() => {
    loadTowerAdmins();
  }, [loadTowerAdmins]);

  const handleApprove = async (id) => {
    try {
      const res = await fetch(`${API}/api/admin/tower-admin/approve/${id}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Failed to approve");
      setTowerAdmins((prev) => prev.filter((a) => a.id !== id));
      toast.success("Tower Admin approved successfully");
    } catch (err) {
      toast.error("Error: " + err.message);
    }
  };

  const handleReject = async (id) => {
    try {
      const res = await fetch(`${API}/api/admin/tower-admin/reject/${id}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Failed to reject");
      setTowerAdmins((prev) => prev.filter((a) => a.id !== id));
      toast.success("Tower Admin rejected successfully");
    } catch (err) {
      toast.error("Error: " + err.message);
    }
  };

  const getTabs = () => {
    const count = activeTab === "pending" ? towerAdmins.length : 0;
    return [
      { id: "pending", label: `⏳ Pending${activeTab === "pending" ? ` (${count})` : ""}` },
      { id: "approved", label: "✅ Approved" }
    ];
  };

  return (
    <div className="admin-layout">
      <AdminSidebar />

      <div className="admin-content">
        <h1>Tower Admin Requests</h1>
        <p className="admin-sub">Approve or reject tower admin sign-ups</p>

        {/* Tabs */}
        <div className="resident-tabs">
          {getTabs().map((tab) => (
            <button
              key={tab.id}
              className={`resident-tab ${activeTab === tab.id ? "active" : ""}`}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content */}
        {error && <div className="admin-error">{error}</div>}

        {loading ? (
          <div className="admin-loading">Loading tower admins...</div>
        ) : towerAdmins.length === 0 ? (
          <div className="admin-empty">
            No {activeTab} tower admin requests
          </div>
        ) : (
          <div className="residents-table-wrapper">
            <table className="residents-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Tower</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {towerAdmins.map((admin) => (
                  <tr key={admin.id}>
                    <td>{admin.name}</td>
                    <td>{admin.email}</td>
                    <td>{admin.phone || "-"}</td>
                    <td>{admin.tower}</td>
                    <td className="action-cell">
                      {activeTab === "pending" && (
                        <>
                          <button
                            className="btn-approve"
                            onClick={() => handleApprove(admin.id)}
                          >
                            Approve
                          </button>
                          <button
                            className="btn-reject"
                            onClick={() => handleReject(admin.id)}
                          >
                            Reject
                          </button>
                        </>
                      )}
                      {activeTab !== "pending" && <span>-</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}